import { Avatar, Badge, Button, Card, CardHeader, EmptyState, Icon, SegmentedTabs, Skeleton, Table, formatDate, formatMoney, formatTime, type Column } from "@helmr/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { isApiError } from "../../api/client";
import { getMember, listMemberBills, type Bill, type Member } from "../../api/members";
import { getPackBalance, listMemberCheckins, type Checkin } from "../../api/checkins";
import { listMemberPlans, type Plan } from "../../api/plans";
import { NoAccess } from "../../app/NoAccess";
import { areaAllows } from "../../app/nav";
import { today as dateOf } from "../../lib/dates";
import { isUnmasked, telHref, whatsappHref } from "../../lib/phone";
import { Chips, Problem, RowsSkeleton, dayMonthYear, useArea } from "./common";
import { EditMemberDrawer, LoginCodeDrawer } from "./MemberDrawers";
import { PlanActionDrawer, counterHref, type PlanActionKind } from "./PlanDrawers";
import { KIND_LABEL, STATUS_TONE, goodToTrain, profileChips } from "./status";
import { reasonText, t } from "./strings";
import s from "./members.module.css";

type Tab = "overview" | "plans" | "checkins" | "bills";
const P = t.profile;
const TABS: Tab[] = ["overview", "plans", "checkins", "bills"];

const isOpenPlan = (p: Plan) => p.lifecycle === "active" && p.status !== "expired" && p.status !== "exhausted";

export function ProfilePage() {
  const { id = "" } = useParams();
  const { client, ctx, today, scope } = useArea();
  const [params, setParams] = useSearchParams();
  const tab = (TABS as string[]).includes(params.get("tab") ?? "") ? (params.get("tab") as Tab) : "overview";
  const [action, setAction] = useState<{ kind: PlanActionKind; plan: Plan } | null>(null);
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState(false);

  const profile = useQuery({ queryKey: ["member", ...scope, id], queryFn: () => getMember(client, id), retry: false, enabled: ctx.status !== "loading" });
  const plansQ = useQuery({ queryKey: ["member-plans", ...scope, id], queryFn: () => listMemberPlans(client, id), retry: false, enabled: ctx.status !== "loading" });

  const setTab = (next: Tab) => setParams((p) => { const n = new URLSearchParams(p); if (next === "overview") n.delete("tab"); else n.set("tab", next); return n; }, { replace: true });

  if (ctx.status !== "loading" && !areaAllows("members", ctx.has)) return <NoAccess />;

  if (profile.isPending || plansQ.isPending && !plansQ.error) return <ProfileSkeleton />;
  if (profile.error) {
    if (isApiError(profile.error) && profile.error.status === 404) {
      return <Card><EmptyState icon="users" title={P.notFoundTitle} description={P.notFoundBody} action={<Button as={Link} to="/members">{P.back}</Button>} /></Card>;
    }
    return <Card><Problem compact={false} error={profile.error} title={P.errorTitle} onRetry={() => void profile.refetch()} /></Card>;
  }

  const { member, balanceDue, walletBalance } = profile.data;
  const plans = plansQ.data ?? [];
  const seesBalance = ctx.has("revenue.member_balance");
  const memberships = plans.filter((p) => p.kind === "membership");
  // Header actions mean the plan running today; a renewal queued behind it only if nothing is running.
  const open = memberships.filter(isOpenPlan);
  const running = open.filter((p) => p.status !== "upcoming").sort((a, b) => b.endDate.localeCompare(a.endDate))[0];
  const target = running ?? open.sort((a, b) => a.startDate.localeCompare(b.startDate))[0];
  const lastMembership = [...memberships].sort((a, b) => b.endDate.localeCompare(a.endDate))[0];
  // No plans in hand is "unknown", never "no plan": that would turn a paying member away.
  const plansUnread = !!plansQ.error && !plansQ.data;
  const answer = goodToTrain(plans, today, ctx.locale);
  const showPhoneActions = ctx.has("members.see_phone") && isUnmasked(member.phone);
  const branchName = ctx.me?.branches.find((b) => b.id === member.homeBranchId)?.name;

  const openAction = (kind: PlanActionKind, plan: Plan | undefined) => { if (plan) setAction({ kind, plan }); };

  return (
    <div className={s.page}>
      <div>
        <Link to="/members" className={s.back}>{P.back}</Link>
      </div>

      <div className={s.hero}>
        <div>
          <div className={s.who}>
            <Avatar id={member.id} name={member.name} />
            <div style={{ minWidth: 0 }}>
              <h1 tabIndex={-1} title={member.name}>{member.name}</h1>
              <small className={s.mono}>{member.phone}{branchName ? ` · ${branchName}` : ""}</small>
            </div>
          </div>
          <div className={s.train} data-testid="good-to-train">
            <span className={s.light} data-tone={plansUnread ? "warn" : answer.tone} aria-hidden="true" />
            {plansUnread ? (
              <>
                <span>{P.plansError}</span>
                <Button size="sm" onClick={() => void plansQ.refetch()}>Retry</Button>
              </>
            ) : (
              <>
                <span>{answer.tone === "ok" ? P.goodToTrain : P.notGood}: {answer.headline}</span>
                <span className={s.why}>{answer.reason}</span>
              </>
            )}
          </div>
          {plansUnread ? null : <div className={s.chips} data-testid="chips"><Chips chips={profileChips(plans, seesBalance ? balanceDue : null, today)} /></div>}
        </div>
        <div className={s.qa}>
          {ctx.has("billing.collect_payment") ? <Button onClick={() => setTab("bills")}>{P.actions.collect}</Button> : null}
          {ctx.has("billing.renew") && lastMembership ? <Button as={Link} to={counterHref({ memberId: member.id, planId: lastMembership.id, action: "renew" })}>{P.actions.renew}</Button> : null}
          {ctx.has("members.freeze") && target ? <Button onClick={() => openAction("freeze", target)}>{P.actions.freeze}</Button> : null}
          {ctx.has("members.extend") && target ? <Button onClick={() => openAction("extend", target)}>{P.actions.extend}</Button> : null}
          {ctx.has("pt.book") ? <Button as={Link} to={`/classes?view=pt&memberId=${member.id}`}>{P.actions.pt}</Button> : null}
          {ctx.has("members.frontdesk_code") && showPhoneActions ? <Button onClick={() => setCode(true)}>{P.actions.code}</Button> : null}
          {showPhoneActions ? <Button as="a" href={telHref(member.phone)} aria-label={`Call ${member.name}`}><Icon name="phone" />Call</Button> : null}
          {showPhoneActions ? <Button as="a" href={whatsappHref(member.phone)} target="_blank" rel="noreferrer" aria-label={`WhatsApp ${member.name}`}><Icon name="chat" />WhatsApp</Button> : null}
        </div>
      </div>

      <SegmentedTabs label="Member sections" value={tab} onChange={setTab} options={TABS.map((v) => ({ value: v, label: P.tabs[v] }))} />

      {tab === "overview" ? <Overview member={member} plans={plans} balanceDue={balanceDue} walletBalance={walletBalance} onEdit={() => setEditing(true)} /> : null}
      {tab === "plans" ? <PlansTab q={plansQ} plans={plans} member={member} onAction={(kind, plan) => setAction({ kind, plan })} /> : null}
      {tab === "checkins" ? <CheckinsTab memberId={member.id} /> : null}
      {tab === "bills" ? <BillsTab memberId={member.id} /> : null}

      {action ? <PlanActionDrawer kind={action.kind} plan={action.plan} member={{ id: member.id, name: member.name }} onClose={() => setAction(null)} /> : null}
      {editing ? <EditMemberDrawer member={member} onClose={() => setEditing(false)} /> : null}
      {code ? <LoginCodeDrawer member={member} onClose={() => setCode(false)} /> : null}
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className={s.page} role="status" aria-busy="true" aria-label="Loading member">
      <div className={s.hero}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div className={s.who}><Skeleton width={36} height={36} style={{ borderRadius: 18 }} /><Skeleton width={220} height={22} /></div>
          <Skeleton width={280} height={14} />
          <Skeleton width={160} height={20} />
        </div>
      </div>
      <Skeleton width={320} height={30} />
      <Card><RowsSkeleton rows={4} label="Loading" /></Card>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------ overview */

function Overview({ member, plans, balanceDue, walletBalance, onEdit }: { member: Member; plans: Plan[]; balanceDue: number; walletBalance: number; onEdit: () => void }) {
  const { ctx } = useArea();
  const money = (n: number) => formatMoney(n, ctx.me?.business.currency ?? "INR", ctx.locale);
  const seesBalance = ctx.has("revenue.member_balance");
  const packs = plans.filter((p) => p.kind === "pt_pack" && isOpenPlan(p));
  const branch = ctx.me?.branches.find((b) => b.id === member.homeBranchId)?.name;
  return (
    <div className={s.cols}>
      <div className={s.stack}>
        <Card>
          <CardHeader title={P.details} action={ctx.has("members.edit_profile") ? <Button size="sm" onClick={onEdit}>{P.edit}</Button> : undefined} />
          <dl className={s.dl}>
            <dt>{P.phone}</dt><dd className={s.mono}>{member.phone}</dd>
            <dt>{P.email}</dt><dd>{member.email ?? <span className={s.muted}>—</span>}</dd>
            <dt>{P.branch}</dt><dd>{branch ?? <span className={s.muted}>—</span>}</dd>
            <dt>{P.joined}</dt><dd>{formatDate(dateOf(member.createdAt, ctx.timeZone), ctx.locale, "short")}</dd>
          </dl>
        </Card>
        <Card>
          <CardHeader title={P.plans} action={<Button size="sm" as={Link} to="?tab=plans">{P.allPlans}</Button>} />
          <PlanList plans={plans.slice().sort((a, b) => b.endDate.localeCompare(a.endDate)).slice(0, 3)} />
        </Card>
      </div>
      <div className={s.stack}>
        {seesBalance ? (
          <Card>
            <CardHeader title="Money" />
            <dl className={s.dl}>
              <dt>{P.balance}</dt><dd className={s.mono} data-testid="balance-due">{money(balanceDue)}</dd>
              <dt>{P.wallet}</dt><dd className={s.mono} data-testid="wallet">{money(walletBalance)}</dd>
            </dl>
          </Card>
        ) : null}
        {ctx.has("pt.pack_balance") ? (
          <Card>
            <CardHeader title={P.packs} />
            {packs.length === 0 ? <p className={s.body}>{P.noPacks}</p> : packs.map((p) => <PackRow key={p.id} plan={p} />)}
          </Card>
        ) : null}
      </div>
    </div>
  );
}

function PackRow({ plan }: { plan: Plan }) {
  const { client, scope } = useArea();
  const q = useQuery({ queryKey: ["pt-balance", ...scope, plan.id], queryFn: () => getPackBalance(client, plan.id), retry: false });
  return (
    <div className={s.pt}>
      <span>{q.isPending ? <Skeleton width={140} /> : q.error ? <span className={s.muted}>{isApiError(q.error) ? q.error.message : "Balance unavailable"}</span> : P.packLeft(q.data.balance.remaining, q.data.balance.total)}</span>
      <span className={s.muted}>{q.data && q.data.balance.booked > 0 ? P.packBooked(q.data.balance.booked) : null}</span>
    </div>
  );
}

/* --------------------------------------------------------------------------------------------- plans */

function PlanList({ plans, onAction }: { plans: Plan[]; onAction?: (kind: PlanActionKind, plan: Plan) => void }) {
  const { ctx, today } = useArea();
  // One lever per plan action (D57): the lever each POST /plans/:id/* route checks.
  const acts = { extend: ctx.has("members.extend"), freeze: ctx.has("members.freeze"), transfer: ctx.has("members.transfer"), cancel: ctx.has("members.cancel_plan") };
  const { me } = ctx;
  if (plans.length === 0) return <EmptyState compact icon="badge" title={P.noPlans} description={P.noPlansBody} />;
  return (
    <>
      {plans.map((p) => {
        const open = isOpenPlan(p);
        const live = p.freezes.find((f) => f.endedAt === null && f.daysPushed > 0 && (f.actualTo ?? f.to) >= today);
        return (
          <div className={s.plan} key={p.id} data-testid="plan">
            <div className={s.planTop}>
              <div>
                <b>{KIND_LABEL[p.kind]}</b>{" "}
                <Badge tone={STATUS_TONE[p.status] ?? "neutral"} dot>{p.status.replace(/^./, (c) => c.toUpperCase())}</Badge>
              </div>
              <span className={s.mono}>{dayMonthYear(p.startDate, ctx.locale)} to {dayMonthYear(p.endDate, ctx.locale)}</span>
            </div>
            <div className={s.planMeta}>
              <span>{p.durationDays} days</span>
              {p.sessionsTotal !== null ? <span>{p.sessionsUsed} of {p.sessionsTotal} sessions used</span> : null}
              {p.compDays > 0 ? <span>{p.compDays} free days added</span> : null}
              {me ? <span>{me.branches.find((b) => b.id === p.branchId)?.name}</span> : null}
            </div>
            {live ? <div className={s.freezeLine}>Frozen {dayMonthYear(live.from, ctx.locale)} to {dayMonthYear(live.actualTo ?? live.to, ctx.locale)}{live.reason ? ` · ${live.reason}` : ""}</div> : null}
            {onAction && open && (acts.extend || acts.freeze || acts.transfer || acts.cancel) ? (
              <div className={s.planActs}>
                {acts.extend ? <Button size="sm" onClick={() => onAction("extend", p)}>{P.actions.extend}</Button> : null}
                {acts.freeze ? <Button size="sm" onClick={() => onAction("freeze", p)}>{P.actions.freeze}</Button> : null}
                {acts.freeze && live ? <Button size="sm" onClick={() => onAction("unfreeze", p)}>{P.actions.unfreeze}</Button> : null}
                {acts.transfer ? <Button size="sm" onClick={() => onAction("transfer", p)}>{P.actions.transfer}</Button> : null}
                {acts.cancel ? <Button size="sm" onClick={() => onAction("cancel", p)}>{P.actions.cancel}</Button> : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

function PlansTab({ q, plans, member, onAction }: { q: { isPending: boolean; error: unknown; refetch: () => unknown }; plans: Plan[]; member: Member; onAction: (kind: PlanActionKind, plan: Plan) => void }) {
  void member;
  const sorted = useMemo(() => plans.slice().sort((a, b) => b.startDate.localeCompare(a.startDate) || b.id.localeCompare(a.id)), [plans]);
  return (
    <Card>
      <CardHeader title={P.plans} />
      {q.error ? <Problem error={q.error} title={P.plansError} onRetry={() => void q.refetch()} /> : (
        <div className={s.scrollCard} data-testid="plans-scroll"><PlanList plans={sorted} onAction={onAction} /></div>
      )}
    </Card>
  );
}

/* ----------------------------------------------------------------------------------------- check-ins */

function CheckinsTab({ memberId }: { memberId: string }) {
  const { client, ctx, scope } = useArea();
  const q = useInfiniteQuery({
    queryKey: ["member-checkins", ...scope, memberId],
    queryFn: ({ pageParam }) => listMemberCheckins(client, memberId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (l) => l.nextCursor,
    retry: false,
  });
  const rows = q.data?.pages.flatMap((p) => p.checkins) ?? [];
  const columns: Column<Checkin>[] = [
    { key: "day", header: "Day", cell: (c) => formatDate(c.businessDate, ctx.locale, "weekday") },
    { key: "time", header: "Time", cell: (c) => <span className={s.mono}>{formatTime(c.scannedAt, ctx.timeZone)}</span> },
    { key: "result", header: "Result", cell: (c) => <Badge tone={c.decision === "allow" ? "ok" : "crit"} dot>{c.decision === "allow" ? "Allowed" : "Denied"}</Badge> },
    { key: "why", header: "Why", cell: (c) => <span className={s.trunc}>{c.reasons.length ? c.reasons.map(reasonText).join(", ") : "—"}</span> },
  ];
  return (
    <Card>
      <CardHeader title={P.checkins} />
      {q.isPending ? <RowsSkeleton rows={5} label="Loading check-ins" />
        : q.error ? <Problem error={q.error} title={t.attendance.checkinsError} onRetry={() => void q.refetch()} />
        : rows.length === 0 ? <EmptyState compact icon="finger" title={P.noCheckins} description={P.noCheckinsBody} />
        : <Table label="Check-ins" columns={columns} rows={rows} rowKey={(c) => c.id} minWidth={520} hasMore={q.hasNextPage} onLoadMore={() => void q.fetchNextPage()} loadingMore={q.isFetchingNextPage} />}
    </Card>
  );
}

/* --------------------------------------------------------------------------------------------- bills */

function BillsTab({ memberId }: { memberId: string }) {
  const { client, ctx, scope } = useArea();
  const allowed = ctx.has("billing.bills_view");
  const seesAmounts = ctx.has("revenue.bill_amounts");
  const q = useInfiniteQuery({
    queryKey: ["member-bills", ...scope, memberId],
    queryFn: ({ pageParam }) => listMemberBills(client, memberId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (l) => l.nextCursor,
    enabled: allowed,
    retry: false,
  });
  if (!allowed) return <Card><EmptyState compact icon="bill" title={P.bills} description={P.notAllowedBills} /></Card>;
  const rows = q.data?.pages.flatMap((p) => p.bills) ?? [];
  const money = (n: number, cur: string) => formatMoney(n, cur, ctx.locale);
  const columns: Column<Bill>[] = [
    { key: "date", header: "Date", cell: (b) => formatDate(b.businessDate, ctx.locale, "short") },
    ...(seesAmounts ? [
      { key: "total", header: "Total", align: "end" as const, cell: (b: Bill) => <span className={s.mono}>{money(b.total, b.currency)}</span> },
      { key: "bal", header: "Balance", align: "end" as const, cell: (b: Bill) => <span className={s.mono}>{money(b.balance, b.currency)}</span> },
    ] : []),
    { key: "status", header: "Status", cell: (b) => <Badge tone={b.status === "void" ? "neutral" : b.paymentStatus === "paid" ? "ok" : b.paymentStatus === "partial" ? "warn" : "crit"} dot>{b.status === "void" ? "Void" : b.paymentStatus.replace(/^./, (c) => c.toUpperCase())}</Badge> },
    { key: "open", header: P.open, srOnly: true, cell: (b) => <Link to={`/billing/bills/${b.id}`}>{P.open}</Link> },
  ];
  return (
    <Card>
      <CardHeader title={P.bills} />
      {q.isPending ? <RowsSkeleton rows={4} label="Loading bills" />
        : q.error ? <Problem error={q.error} title={P.billsError} onRetry={() => void q.refetch()} />
        : rows.length === 0 ? <EmptyState compact icon="bill" title={P.noBills} description={P.noBillsBody} />
        : <Table label="Bills" columns={columns} rows={rows} rowKey={(b) => b.id} minWidth={480} hasMore={q.hasNextPage} onLoadMore={() => void q.fetchNextPage()} loadingMore={q.isFetchingNextPage} />}
    </Card>
  );
}
